// @flow

import styles from './SearchResultPage.less';
import get from 'lodash/get';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import classnames from 'classnames';
import {
  AsidedLayout,
  Loader,
  PageBlock,
  Card,
  Paragraph,
  Secondary
} from 'seek-style-guide/react';

import SearchResultSummary from './SearchResultSummary/SearchResultSummary';
import SearchResultList from './SearchResultList/SearchResultList';
import TieredSearchResultList from './TieredSearchResultList/TieredSearchResultList';
import ZeroResults from './ZeroResults/ZeroResults';
import ResultsError from './ResultsError/ResultsError';
import Pagination from './Pagination/Pagination';
import SearchResultHeader from './SearchResultHeader/SearchResultHeader';
import DidYouMean from './DidYouMean/DidYouMean';
import CompanyResults from './CompanyResults/CompanyResults';
import CompanySummary from './CompanySummary/CompanySummary';
import Lmis from 'src/components/Lmis/Lmis';
import SavePanel from './SavePanel/SavePanel';
import SeoFooter from './SeoFooter/SeoFooter';
import SideBar from './SideBar/SideBar';
import StickySerpTarget from 'src/components/StickySerpTarget/StickySerpTarget';

type Props = {
  authenticated: boolean,
  searchResultsViewModel?: SearchViewModel,
  saveable: boolean,
  isLoading: boolean,
  isError: boolean,
  source?: string,
  resultsHidden: boolean,
  locale: Locale,
  canonicalCompany?: Description,
  companySuggestion?: SearchViewModelCompanySuggestion,
  pathname: string,
  pageNumber: number,
  relatedSearches?: Array<RelatedSearchKeyword>,
  clearCompanyRefinements?: SearchParams | boolean,
  uniqueSearchToken?: string,
  shouldShowNudge: boolean,
  solMetadataString?: string
};

const getRootClasses = hideResults =>
  classnames({
    [styles.root]: true,
    [styles.root_areResultsHidden]: hideResults
  });

const combineJobsAndTiers = (jobs, tiers) => [...jobs, ...tiers];

const flattenTiers = tiers =>
  tiers.reduce((arr, tier) => [...arr, ...tier.jobs], []);

const mapStateToProps = ({
  results,
  search,
  location: { locale, pathname, pageNumber },
  saveSearch,
  user,
  nudges
}) => ({
  searchResultsViewModel: results.results,
  companySuggestion: results.companySuggestion,
  canonicalCompany: results.canonicalCompany,
  saveable: saveSearch.saveable,
  locale,
  pathname,
  pageNumber,
  isLoading: results.isLoading,
  isError: results.isError,
  source: results.source,
  resultsHidden: results.hidden,
  uniqueSearchToken: results ? results.uniqueSearchToken : null,
  authenticated: user.authenticated,
  relatedSearches: results.relatedSearches,
  clearCompanyRefinements: get(search, 'refinements.clear.company'),
  shouldShowNudge: nudges.nudges.length > 0,
  solMetadataString: results.solMetadataString
});

class SearchResultPage extends Component<Props> {
  static defaultProps = {
    relatedSearches: [],
    clearCompanyRefinements: false
  };

  renderSideBar = () => <SideBar />;

  renderLoader(isLoading: boolean) {
    const loaderClasses = classnames({
      [styles.loader]: true,
      [styles.loader_isHidden]: !isLoading
    });

    return (
      <div className={loaderClasses} data-automation="searchResultsLoader">
        <Loader />
      </div>
    );
  }

  renderResults(viewModel?: SearchViewModel) {
    const {
      isZeroResults,
      jobs,
      header,
      tiers,
      hasHeaderBeforeJobs,
      searchParams = {},
      suggestions
    } = viewModel || {};

    const {
      isError,
      saveable,
      authenticated,
      canonicalCompany,
      companySuggestion,
      clearCompanyRefinements,
      uniqueSearchToken,
      shouldShowNudge,
      source,
      isLoading
    } = this.props;

    const hasResults = !isError && !isZeroResults;

    const combinedJobsAndTiers = combineJobsAndTiers(
      jobs || [],
      flattenTiers(tiers || [])
    );

    const resultsClassName = classnames({
      [styles.separator]: hasHeaderBeforeJobs
    });

    return (
      <div key="content" data-automation="searchResults">
        {isError && <ResultsError />}
        {isZeroResults ? (
          <ZeroResults companySuggestion={companySuggestion} />
        ) : null}
        {hasResults && (
          <div className={resultsClassName} key={uniqueSearchToken}>
            {header && <SearchResultHeader message={header} />}
            {suggestions && <DidYouMean locations={suggestions} />}
            {saveable && !authenticated && !hasHeaderBeforeJobs && (
              <SavePanel position="top" {...{ authenticated }} />
            )}
            {searchParams && searchParams.companysearch ? (
              <CompanySummary canonicalCompany={canonicalCompany} />
            ) : null}
            {typeof clearCompanyRefinements === 'object' && (
              <CompanyResults
                clearCompanyRefinements={clearCompanyRefinements}
                jobs={combinedJobsAndTiers}
              />
            )}
            {tiers && <TieredSearchResultList header={header} tiers={tiers} />}
            {jobs && (
              <SearchResultList jobs={jobs} showNudge={shouldShowNudge} />
            )}
            <div className={styles.flex}>
              <div className={styles.savePanel}>
                {saveable && (
                  <SavePanel position="bottom" {...{ authenticated }} />
                )}
              </div>
              {!isLoading && (
                <div className={styles.lmis}>
                  <Lmis showDefault />
                </div>
              )}
              <Card transparent className={styles.pagination}>
                <Pagination />
              </Card>
            </div>
          </div>
        )}
        {source && (
          <Paragraph>
            <Secondary>{`Source: ${source}`}</Secondary>
          </Paragraph>
        )}
      </div>
    );
  }

  render() {
    const {
      isLoading,
      resultsHidden,
      searchResultsViewModel,
      locale,
      pathname,
      pageNumber,
      relatedSearches,
      companySuggestion,
      solMetadataString
    } = this.props;

    if (resultsHidden) {
      return (
        <section className={getRootClasses(resultsHidden)}>
          <span
            data-automation="hiddenSearchResults"
            data-sol-meta={solMetadataString}
          />
        </section>
      );
    }

    const { summary, isZeroResults = false, searchParams } =
      searchResultsViewModel || {};

    return (
      <StickySerpTarget
        component="section"
        role="region"
        aria-label="Search Results"
        className={getRootClasses(isLoading)}
        name="sticky-save-search-desktop"
      >
        {/* SeoFooter needs to be high in the DOM for google */}
        <SeoFooter
          {...{ locale, pathname, pageNumber, relatedSearches }}
          className={styles.flexSendToBottom}
        />
        <PageBlock className={styles.searchSummaryContainer}>
          <SearchResultSummary
            {...{
              isLoading,
              ...summary,
              companySuggestion,
              isZeroResults,
              searchParams,
              solMetadataString
            }}
          />
          <AsidedLayout renderAside={this.renderSideBar} size="180px">
            <div className={classnames(styles.content, styles.loaderContainer)}>
              {this.renderLoader(isLoading)}
              {this.renderResults(searchResultsViewModel)}
            </div>
          </AsidedLayout>
        </PageBlock>
      </StickySerpTarget>
    );
  }
}

export default connect(mapStateToProps)(SearchResultPage);
